const acknowledgeContent = (state, id) => ({
  ...state,
  [id]: {
    ...state[id],
    acknowledgement: {
      ...state[id].acknowledgement,
      acknowledged_at: new Date(),
    },
  },
});

const unacknowledgeContent = (state, id) => ({
  ...state,
  [id]: {
    ...state[id],
    acknowledgement: {
      ...state[id].acknowledgement,
      acknowledged_at: null,
    },
  },
});

// A local engagement field should only ever win over a fresh server response
// for a few seconds after the user's own action on that specific field —
// long enough to survive a single lagging read-replica refetch, far too
// short to ever mistake a stale rehydrated React Query cache entry
// (patron.jsx persists queries for 24h) for a recent local mutation.
// Tracked per field *group* (like vs. bookmark), not one shared flag —
// otherwise bookmarking a stale entry would also freeze its unrelated
// reaction state against a fresh response arriving in the same window.
const RECENT_MUTATION_WINDOW_MS = 10_000;
const hasRecentLocalMutation = (entry, markerField) =>
  !!entry?.[markerField] &&
  Date.now() - entry[markerField] < RECENT_MUTATION_WINDOW_MS;

// FE-39417: title/description/body_excerpt are fetched by several writers (feed, search,
// translate, related...) into the same contents[id]. A writer that did not request
// mention_format=with_metadata returns the flat `@Name`, and whichever lands last wins —
// which would silently unlink every mention on the card. Mirrors pony's preserve_mentions
// echo guard: keep the stored tokenised value only when the incoming one is exactly its
// flattened form. Any other difference (translation, untranslation, a real edit) is accepted.
const MENTION_GUARDED_FIELDS = ['title', 'description', 'body_excerpt'];
const FULL_MENTION = /@\{ref:user::id:\d+::name:[^}]+\}/;
const FULL_MENTION_G = /@\{ref:user::id:(\d+)::name:([^}]+)\}/g;

const keepStoredMentions = (existing, content) => {
  if (!existing) return content;

  let result = content;
  MENTION_GUARDED_FIELDS.forEach((field) => {
    const stored = existing[field];
    const incoming = content[field];
    if (typeof stored !== 'string' || typeof incoming !== 'string') return;
    if (FULL_MENTION.test(incoming)) return;
    if (!FULL_MENTION.test(stored)) return;

    const flattenedStored = stored.replace(
      FULL_MENTION_G,
      (_match, _id, name) => `@${name}`
    );
    if (flattenedStored === incoming) {
      result = result === content ? { ...content } : result;
      result[field] = stored;
    }
  });
  return result;
};

const addContent = (state, rawContent) => {
  const existing = state[rawContent.id];
  const content = keepStoredMentions(existing, rawContent);
  if (!existing) return { ...state, [content.id]: content };

  // Some sources (e.g. sparse payloads missing pdf_export_enabled — FE-35715)
  // omit keys a prior, richer record already set. Only overwrite when this
  // entry actually carries the key — otherwise keep whatever's already
  // stored. Assumes server-origin content objects, where an absent key (not
  // requested) and an explicit `undefined` value never occur together.
  const pdfExportEnabled = Object.prototype.hasOwnProperty.call(
    content,
    'pdf_export_enabled'
  )
    ? content.pdf_export_enabled
    : existing.pdf_export_enabled;

  return {
    ...state,
    [content.id]: {
      ...content,
      pdf_export_enabled: pdfExportEnabled,
    },
  };
};

const addContents = (state, contents) => {
  const newContents = contents.reduce((obj, rawContent) => {
    // Check obj (this dispatch) before state (the prior one) — the same
    // content can appear twice in one dispatch when it's returned by more
    // than one homepage module.
    const existing = obj[rawContent.id] || state[rawContent.id];
    const content = keepStoredMentions(existing, rawContent);
    if (!existing) return { ...obj, [content.id]: content };

    // Preserve local engagement state only when `existing` is a recent local
    // mutation of that specific field group — like/react and bookmark are
    // tracked independently so one doesn't shield the other from a fresh
    // response.
    const preserveLiked = hasRecentLocalMutation(existing, '_optimisticLikeAt');
    const preserveBookmark = hasRecentLocalMutation(
      existing,
      '_optimisticBookmarkAt'
    );
    const incomingLikes = content.reaction_counts?.likes;
    const liked = preserveLiked ? existing.liked : content.liked;
    const bookmarked = preserveBookmark
      ? existing.bookmarked
      : content.bookmarked;
    const likes = preserveLiked
      ? existing.reaction_counts?.likes ?? incomingLikes
      : incomingLikes;
    const expandedReactions = preserveLiked
      ? existing.expanded_reactions ?? content.expanded_reactions
      : content.expanded_reactions;

    // Some homepage modules opt into body_excerpt (include_body_excerpt) and
    // some don't, so the same content can arrive with and without it. Only
    // overwrite when this entry actually carries the key — otherwise keep
    // whatever an already-processed richer entry set (FE-32783). Assumes
    // server-origin content objects, where an absent key (not requested) and
    // an explicit `undefined` value never occur together.
    const bodyExcerpt = Object.prototype.hasOwnProperty.call(
      content,
      'body_excerpt'
    )
      ? content.body_excerpt
      : existing.body_excerpt;

    // Gateways can drop pdf_export_enabled on sparse payloads (FE-35715) the
    // same way some homepage modules omit body_excerpt above — only overwrite
    // when this entry actually carries the key, otherwise keep whatever an
    // already-processed richer entry set. Same server-origin-object
    // assumption as body_excerpt: an absent key and an explicit `undefined`
    // never occur together.
    const pdfExportEnabled = Object.prototype.hasOwnProperty.call(
      content,
      'pdf_export_enabled'
    )
      ? content.pdf_export_enabled
      : existing.pdf_export_enabled;

    return {
      ...obj,
      [content.id]: {
        ...content,
        liked,
        bookmarked,
        reaction_counts: {
          ...content.reaction_counts,
          likes,
        },
        expanded_reactions: expandedReactions,
        // Carry each marker forward while it's still protecting local state,
        // so a second stale dispatch within the same window is still guarded.
        ...(preserveLiked && {
          _optimisticLikeAt: existing._optimisticLikeAt,
        }),
        ...(preserveBookmark && {
          _optimisticBookmarkAt: existing._optimisticBookmarkAt,
        }),
        body_excerpt: bodyExcerpt,
        pdf_export_enabled: pdfExportEnabled,
      },
    };
  }, {});

  return {
    ...state,
    ...newContents,
  };
};

const likeContent = (state, id) => {
  const likeCount = state[id].reaction_counts.likes || 0;

  return {
    ...state,
    [id]: {
      ...state[id],
      liked: true,
      reaction_counts: {
        ...state[id].reaction_counts,
        likes: likeCount + 1,
      },
      _optimisticLikeAt: Date.now(),
    },
  };
};

const unlikeContent = (state, id) => {
  // Defaulting to 1 because we're immediately going to subtract a like, avoid "-1 Likes"
  const likeCount = state[id].reaction_counts.likes || 1;

  return {
    ...state,
    [id]: {
      ...state[id],
      liked: false,
      reaction_counts: {
        ...state[id].reaction_counts,
        likes: likeCount - 1,
      },
      _optimisticLikeAt: Date.now(),
    },
  };
};

const bookmarkContent = (state, id) => ({
  ...state,
  [id]: {
    ...state[id],
    bookmarked: true,
    _optimisticBookmarkAt: Date.now(),
  },
});

const unbookmarkContent = (state, id) => ({
  ...state,
  [id]: {
    ...state[id],
    bookmarked: false,
    _optimisticBookmarkAt: Date.now(),
  },
});

const updateContentCommentsCount = (state, id, val) => {
  const count = state[id].comment_count + val;
  return updateContent(state, id, { comment_count: count });
};

const updateContent = (state, id, attrs = {}) => ({
  ...state,
  [id]: {
    ...state[id],
    ...attrs,
  },
});

export default {
  acknowledgeContent,
  unacknowledgeContent,
  addContent,
  addContents,
  likeContent,
  unlikeContent,
  bookmarkContent,
  unbookmarkContent,
  updateContentCommentsCount,
  updateContent,
  RECENT_MUTATION_WINDOW_MS,
};
